import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ImBookmark } from 'react-icons/im';
import { TbStarFilled } from 'react-icons/tb';
import { FaTrash } from 'react-icons/fa';
import { FaEdit } from 'react-icons/fa';
import {
	editSavedSearch,
	getListPropertyBookMarked,
	getListSavedSearch,
	removePropertyBookMarked,
	removeSaveSearch,
} from '@services';
import {  SaveCustomSearch } from '@components';
import { AppString, myDashboardformatDate, toastSuccessHelper } from '@utils';
import { useDispatch } from 'react-redux';
import { setAddressState, setCustomSearchOptionState } from '@/store/slice';
import { useNavigate } from 'react-router-dom';
import { Category, Filter, FilterObject } from '@interface';

interface Property {
	bbl: string;
	name: string;
	borough: string;
	postalcode: string;
}

interface SavedSearch {
	name: string;
	createdAt: string;
	housingType: string;
	housingFilters: Filter[];
	addFilters: FilterObject[];
	logicalOperator: 'AND' | 'OR';
	navigateParam: string;
	_id: string;
}

export const MyDashboard: React.FC = () => {
	const [bookmarkedProperties, setBookmarkedProperties] = useState<
		Array<Property>
	>([]);
	const [savedSearches, setSavedSearches] = useState<Array<SavedSearch>>([]);
	const [isLoadingBookmarks, setIsLoadingBookmarks] = useState<boolean>(false);
	const [isLoadingSearches, setIsLoadingSearches] = useState<boolean>(false);
	const [error, setError] = useState<{
		bookmarks: string | null;
		searches: string | null;
	}>({
		bookmarks: null,
		searches: null,
	});
	const [deletingBbls, setDeletingBbls] = useState<string[]>([]);
	const [deletingSearchIds, setDeletingSearchIds] = useState<string[]>([]);
	const [fullName, setFullName] = useState<string>('');
	const [queryName, setQueryName] = useState<string>('');
	const [customSearchId, setCustomSearchId] = useState<string>('');
	const [isModelOpen, setIsModelOpen] = useState<boolean>(false);

	const dispatch = useDispatch();
	const navigate = useNavigate();

	// Reference to help track which rows are being removed
	const rowRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({});
	const modalRef = useRef<HTMLDivElement | null>(null);

	const closeModal = useCallback(() => setIsModelOpen(false), []);

	useEffect(() => {
		const name = localStorage.getItem(AppString.full_name);
		setFullName(name || '');
		getBookMarkedProperties();
		getCustomSearchProperties();
	}, []);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				modalRef.current &&
				!modalRef.current.contains(event.target as Node)
			) {
				closeModal();
			}
		};

		if (isModelOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isModelOpen, closeModal]);

	const getBookMarkedProperties = async () => {
		setIsLoadingBookmarks(true);
		setError((prev) => ({ ...prev, bookmarks: null }));

		try {
			const response = await getListPropertyBookMarked();
			if (response.status === 200) {
				setBookmarkedProperties(response?.data?.data || []);
			}
		} catch (e) {
			setError((prev) => ({
				...prev,
				bookmarks: 'Failed to load bookmarked properties',
			}));
			console.error('Error fetching bookmarked properties:', e);
		} finally {
			setIsLoadingBookmarks(false);
		}
	};

	const getCustomSearchProperties = async () => {
		setIsLoadingSearches(true);
		setError((prev) => ({ ...prev, searches: null }));

		try {
			const response = await getListSavedSearch();
			if (response.status === 200) {
				setSavedSearches(response?.data?.data || []);
			}
		} catch (e) {
			setError((prev) => ({
				...prev,
				searches: 'Failed to load saved searches',
			}));
			console.error('Error fetching saved searches:', e);
		} finally {
			setIsLoadingSearches(false);
		}
	};

	const handleBookmarkDelete = async (bbl: string) => {
		if (!bbl) return;

		const result = window.confirm(
			'Are you sure you want to delete this bookmark?'
		);
		if (!result) return;

		setDeletingBbls((prev) => [...prev, bbl]);

		// Apply fade-out animation
		if (rowRefs.current[bbl]) {
			rowRefs.current[bbl]?.classList.add(
				'opacity-50',
				'transition-opacity',
				'duration-300'
			);
		}

		try {
			const response = await removePropertyBookMarked(bbl);
			if (response.status === 200) {
				// Wait for animation to complete then update state
				setTimeout(() => {
					setBookmarkedProperties((prevProperties) =>
						prevProperties.filter((property) => property.bbl !== bbl)
					);
					setDeletingBbls((prev) => prev.filter((id) => id !== bbl));
				}, 300);
			}
		} catch (e) {
			console.error('Error removing bookmark:', e);
			setError((prev) => ({
				...prev,
				bookmarks: 'Failed to delete property bookmark',
			}));
			// Remove from deleting list if failed
			setDeletingBbls((prev) => prev.filter((id) => id !== bbl));
			// Revert animation
			if (rowRefs.current[bbl]) {
				rowRefs.current[bbl]?.classList.remove(
					'opacity-50',
					'transition-opacity',
					'duration-300'
				);
			}
		}
	};

	const handlePropertyClick = (property: Property) => {
		const { name, borough, postalcode, bbl } = property;
		dispatch(
			setAddressState({
				properties: {
					name,
					borough,
					postalcode,
					label: '',
					addendum: {
						pad: { bbl },
					},
				},
			})
		);
		navigate('/property', { replace: true });
	};

	const handleSavedSearchClick = (search: SavedSearch) => {
		const {
			addFilters,
			housingType,
			housingFilters,
			navigateParam,
			logicalOperator,
		} = search;
		dispatch(
			setCustomSearchOptionState({
				SelectedHousingType: housingType as Category,
				Housingfilters: housingFilters,
				Addfilters: addFilters,
				LogicalOperator: logicalOperator,
				navigateParam,
			})
		);
		navigate(`/search${navigateParam}`, { replace: true });
	};

	const handleSavedSearchDelete = async (id: string) => {
		if (!id) return;

		const result = window.confirm(
			'Are you sure you want to delete this saved search?'
		);
		if (!result) return;

		setDeletingSearchIds((prev) => [...prev, id]);

		// Apply fade-out animation
		if (rowRefs.current[id]) {
			rowRefs.current[id]?.classList.add(
				'opacity-50',
				'transition-opacity',
				'duration-300'
			);
		}

		try {
			const response = await removeSaveSearch(id);
			if (response.status === 200) {
				// Wait for animation to complete then update state
				setTimeout(() => {
					setSavedSearches((prevSearches) =>
						prevSearches.filter((search) => search._id !== id)
					);
					setDeletingSearchIds((prev) =>
						prev.filter((searchId) => searchId !== id)
					);
				}, 300);
				toastSuccessHelper('Saved search deleted successfully');
			}
		} catch (e) {
			console.error('Error removing saved search:', e);
			setError((prev) => ({
				...prev,
				searches: 'Failed to delete saved search',
			}));
			// Remove from deleting list if failed
			setDeletingSearchIds((prev) =>
				prev.filter((searchId) => searchId !== id)
			);
			// Revert animation
			if (rowRefs.current[id]) {
				rowRefs.current[id]?.classList.remove(
					'opacity-50',
					'transition-opacity',
					'duration-300'
				);
			}
		}
	};

	const editCustomSearchDetails = async (name: string) => {
		if (!customSearchId || !name.trim()) return;

		try {
			const response = await editSavedSearch({
				name,
				customSearchId,
			});

			if (response.status === 200) {
				// Update the name in the local state
				setSavedSearches((prevSearches) =>
					prevSearches.map((search) =>
						search._id === customSearchId ? { ...search, name } : search
					)
				);
				toastSuccessHelper('Successfully updated this saved search!');
				closeModal();
			}
		} catch (e: any) {
			const errorMessage = e?.response?.data?.message || 'Something went wrong';
			setError((prev) => ({ ...prev, searches: errorMessage }));
			console.error('Error updating saved search:', e);
		}
	};

	const openEditModal = (search: SavedSearch) => {
		setCustomSearchId(search._id);
		setQueryName(search.name);
		setIsModelOpen(true);
	};

	// Loading placeholder component
	const LoadingPlaceholder = () => (
		<div className="p-4 text-center">
			<div className="flex justify-center items-center space-x-2">
				<div className="w-4 h-4 bg-gray-300 rounded-full animate-bounce"></div>
				<div className="w-4 h-4 bg-gray-300 rounded-full animate-bounce delay-150"></div>
				<div className="w-4 h-4 bg-gray-300 rounded-full animate-bounce delay-300"></div>
			</div>
			<div className="mt-2">Loading...</div>
		</div>
	);

	return (
		<div className="w-full py-8 mx-auto px-4 md:px-6">
			<div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
				<h1 className="text-[24px] font-medium">
					Welcome, <span className="font-bold">{fullName}</span>
				</h1>
			</div>

			{/* Bookmarked Properties Section */}
			<div className="mb-8">
				<h2 className="text-xl font-bold px-2 border-b pb-2">
					Bookmarked Properties
				</h2>
				{error.bookmarks && (
					<div className="text-red-500 p-2 rounded bg-red-50 my-2 transition-all duration-300 ease-in-out">
						{error.bookmarks}
					</div>
				)}

				<div className="overflow-x-auto transition-all duration-300 ease-in-out">
					{isLoadingBookmarks ? (
						<LoadingPlaceholder />
					) : (
						<table className="w-full border-collapse">
							<thead>
								<tr className="bg-white text-left">
									<th className="py-3 font-semibold px-2">Property Address</th>
									<th className="py-3 font-semibold px-2">BBL</th>
									<th className="py-3 font-semibold px-2 text-right">Delete</th>
								</tr>
							</thead>
							<tbody className="transition-all duration-300 ease-in-out">
								{bookmarkedProperties.length === 0 ? (
									<tr>
										<td
											colSpan={3}
											className="py-4 px-2 text-center text-gray-500 italic"
										>
											No bookmarked properties found
										</td>
									</tr>
								) : (
									bookmarkedProperties.map((property, index) => (
										<tr
											key={property.bbl || index}
											ref={(el) => (rowRefs.current[property.bbl] = el)}
											className={`border-t border-gray-200 ${
												index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
											} transition-all duration-300 ease-in-out transform hover:bg-gray-100`}
										>
											<td className="py-4 px-2">
												<div
													className="flex items-center font-bold hover:text-link hover:font-bold transition-colors duration-200 cursor-pointer"
													onClick={() => handlePropertyClick(property)}
												>
													<span className="mr-2">
														<ImBookmark className="h-7 w-7 text-blue-600" />
													</span>
													{`${property.name}, ${property?.borough}${property.borough ? ',' : ''} ${property.postalcode}`}
												</div>
											</td>
											<td className="py-4 px-2">{property.bbl}</td>
											<td className="py-4 px-2 text-right">
												<button
													className="hover:bg-gray-300 p-1 rounded-sm transition-colors duration-200"
													onClick={() => handleBookmarkDelete(property.bbl)}
													disabled={
														deletingBbls.includes(property.bbl) ||
														isLoadingBookmarks
													}
													aria-label="Delete bookmark"
												>
													<FaTrash
														className={`h-6 w-6 ${deletingBbls.includes(property.bbl) ? 'text-gray-400' : 'text-red-500 hover:text-red-700'}`}
													/>
												</button>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					)}
				</div>
			</div>

			{/* Saved Custom Searches Section */}
			<div className="mt-8">
				<h2 className="text-xl font-bold px-2 border-b pb-2">
					Saved Custom Searches
				</h2>
				<div className="overflow-x-auto">
					{isLoadingSearches ? (
						<LoadingPlaceholder />
					) : (
						<table className="w-full border-collapse">
							<thead>
								<tr className="bg-white text-left">
									<th className="py-3 font-semibold px-2">Query name</th>

									<th className="py-3 font-semibold px-2">Saved on</th>
									<th className="py-3 font-semibold px-2 text-right">Edit</th>
									<th className="py-3 font-semibold px-2 text-right">Delete</th>
								</tr>
							</thead>
							<tbody>
								{savedSearches.length === 0 ? (
									<tr>
										<td
											colSpan={4}
											className="py-4 px-2 text-center text-gray-500 italic"
										>
											No Saved search found
										</td>
									</tr>
								) : (
									savedSearches.map((search, index) => (
										<tr
											key={index}
											className={`border-t border-gray-200 ${
												index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
											} transition-all duration-300 ease-in-out hover:bg-gray-100`}
										>
											<td
												className="py-4 px-2"
												onClick={() => handleSavedSearchClick(search)}
											>
												<div className="flex items-center font-bold hover:text-link hover:font-bold transition-colors duration-200">
													<span className="mr-2">
														<TbStarFilled className="h-7 w-7 text-yellow-500" />
													</span>
													{search.name}
												</div>
											</td>

											<td className="py-4 px-2">
												{myDashboardformatDate(search.createdAt)}
											</td>
											<td className="py-4 px-2 text-right">
												<button
													className="hover:bg-gray-300 p-1 rounded-sm transition-colors duration-200"
													onClick={() => openEditModal(search)}
												>
													<FaEdit className="h-6 w-6 text-blue-500 hover:text-blue-700" />
												</button>
											</td>
											<td className="py-4 px-2 text-right">
												<button
													className="hover:bg-gray-300 p-1 rounded-sm transition-colors duration-200"
													onClick={() => handleSavedSearchDelete(search._id)}
													disabled={
														deletingSearchIds.includes(search.navigateParam) ||
														isLoadingSearches
													}
													aria-label="Delete bookmark"
												>
													<FaTrash
														className={`h-6 w-6 ${deletingBbls.includes(search.navigateParam) ? 'text-gray-400' : 'text-red-500 hover:text-red-700'}`}
													/>
												</button>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					)}
				</div>
			</div>
			{isModelOpen && (
				<SaveCustomSearch
					query={queryName || ''}
					onClose={closeModal}
					onSubmit={editCustomSearchDetails}
					modelref={modalRef}
				/>
			)}
		</div>
	);
};
